import React, { useEffect, useState } from 'react'

import { shape, string } from 'prop-types'

import { useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'

import { Hidden, Dialog } from '@material-ui/core'

import Map from '@components/Map'
import PlanCard from '@components/PlanCard'
import PlanSelector from '@components/PlanSelector'
import InputAutocomplete from '@components/InputAutocomplete'
import Divider from '@components/Divider'

import { GymMarker, GymMarkerSelected, Search, Filter } from '@assets/icons'

import useGeolocation from '@hooks/useGeolocation'

import { actions as addressActions } from '@store/ducks/address'
import { actions as gymActions } from '@store/ducks/gym'

import * as Style from './GymSearchMap.style'

export default function GymSearchMap({ icons, theme }) {
  const { language } = useSelector(store => store.application)
  const map = useSelector(store => store.map)
  const gym = useSelector(store => store.gym)
  const predictions = useSelector(store => store.address.predictions)
  const { membershipFee } = useSelector(store => store.country)

  const intl = useIntl()
  const dispatch = useDispatch()
  const position = useGeolocation()

  const [selectedPlan, setSelectedPlan] = useState({})
  const [showSelectPlanMobile, setShowSelectPlanMobile] = useState(false)

  const { handleSubmit, control } = useForm()

  useEffect(() => {
    if (position) {
      dispatch(gymActions.addGymSearchGeolocationFilterRequest(position))
      dispatch(gymActions.gymSearchRequest())
    }
  }, [position])

  function onAddressSubmit(data) {
    dispatch(addressActions.getAddressLocationRequest(data.address))
    dispatch(gymActions.gymSearchRequest())
    return data
  }

  function handleSelectedPlan(plan) {
    dispatch(gymActions.addGymSearchPlanFilterRequest(plan))
    dispatch(gymActions.gymSearchRequest())
    setSelectedPlan(plan)
  }

  function handleTemporarySelectedPlan(plan) {
    setSelectedPlan(plan)
  }

  function applyFilterAndCloseModal() {
    dispatch(gymActions.addGymSearchPlanFilterRequest(selectedPlan))
    dispatch(gymActions.gymSearchRequest())
    setSelectedPlan(selectedPlan)
    setShowSelectPlanMobile(false)
  }

  function onSelectGym(selectedGym) {
    dispatch(gymActions.selectGymRequest(selectedGym))
  }

  function onUnselectGym() {
    dispatch(gymActions.unselectGymRequest())
  }

  function onMapChangeCenter(center) {
    dispatch(gymActions.addGymSearchGeolocationFilterRequest(center))
    dispatch(gymActions.gymSearchRequest())
  }

  function handleChangePredictions(addresses) {
    const [address] = addresses
    if (address) {
      dispatch(addressActions.updatePredictionsRequest(address))
    }
  }

  function handleOnSelectAddress(address) {
    control.setValue('address', address)
    onAddressSubmit({ address })
  }

  function showFullScreenModal(event) {
    event.preventDefault()
    setSelectedPlan(gym.filter.plan)
    setShowSelectPlanMobile(true)
  }

  function onKeyPressed(event) {
    if (event.key === 'Enter') {
      onAddressSubmit({ address: event.target.value })
      dispatch(gymActions.gymSearchRequest())
    }
  }

  return (
    <Style.MapWrapper>
      <Map
        language={language}
        position={gym.filter.geolocation}
        markers={map.markers}
        icon={icons.gymMarker || GymMarker}
        selectedIcon={icons.gymMarkerSelected || GymMarkerSelected}
        onSelectMarker={onSelectGym}
        onUnselectMarker={onUnselectGym}
        selectedGym={gym.selectedGym}
        onSearch={onMapChangeCenter}
        icons={icons}
        theme={theme}
      >
        <Style.AddressFieldWrapper>
          <InputAutocomplete
            name="address"
            label={
              intl.messages[
                'smb_acquisition_gym_search_map.address_filter.placeholder'
              ]
            }
            control={control}
            options={predictions}
            onChange={address => {
              handleChangePredictions(address)
              return address
            }}
            onKeyPress={onKeyPressed}
            onSelect={address => handleOnSelectAddress(address)}
          />
          <Hidden mdDown>
            <Style.AddressFieldAction onClick={handleSubmit(onAddressSubmit)}>
              <img
                src={icons.search ? icons.search : Search}
                alt="Search address"
              />
            </Style.AddressFieldAction>
          </Hidden>
          <Hidden lgUp>
            <Style.AddressFieldAction onClick={showFullScreenModal}>
              <img
                src={icons.filter ? icons.filter : Filter}
                alt="Filter by plans"
              />
            </Style.AddressFieldAction>
          </Hidden>
        </Style.AddressFieldWrapper>

        <Hidden mdDown>
          <Style.FilterByPlanWrapper>
            <PlanSelector
              plans={membershipFee}
              onClick={handleSelectedPlan}
              theme={theme.planSelector}
            />
          </Style.FilterByPlanWrapper>

          <Style.PlanDetailWrapper>
            <PlanCard plan={selectedPlan} theme={theme.planCard} />
          </Style.PlanDetailWrapper>
        </Hidden>

        <Dialog fullScreen open={showSelectPlanMobile}>
          <Style.ModalContent>
            <Style.FilterTitle>
              <FormattedMessage id="smb_acquisition_gym_search_map.plans_filter.title" />
            </Style.FilterTitle>

            <Style.PlanFilterModalWrapper>
              <PlanSelector
                plans={membershipFee}
                onClick={handleTemporarySelectedPlan}
                theme={theme.planSelector}
              />
            </Style.PlanFilterModalWrapper>

            <Style.DividerWrapper>
              <Divider />
            </Style.DividerWrapper>

            <PlanCard plan={selectedPlan} theme={theme.planCard} />
          </Style.ModalContent>

          <Style.ModalAction>
            <Style.ModalButton onClick={applyFilterAndCloseModal} full>
              <FormattedMessage id="smb_acquisition_gym_search_map.plans_filter.call_to_action.default" />
            </Style.ModalButton>
          </Style.ModalAction>
        </Dialog>
      </Map>
    </Style.MapWrapper>
  )
}

GymSearchMap.propTypes = {
  icons: shape({
    gymMarker: string,
    gymMarkerSelected: string,
    search: string,
    filter: string,
    aim: string,
    zoomIn: string,
    zoomOut: string,
  }),
  theme: shape({
    searchInArea: shape({
      buttonText: shape({
        color: string,
      }),
    }),
    planSelector: shape({
      planSelectorItem: shape({
        span: shape({
          backgroundColor: string,
        }),
      }),
    }),
  }),
}

GymSearchMap.defaultProps = {
  icons: {},
  theme: {
    searchInArea: {
      buttonText: {
        color: '',
      },
    },
    planSelector: {
      planSelectorItem: {
        span: {
          backgroundColor: '',
        },
      },
    },
  },
}
